import { get, isString, safeJsonStringify } from '@direct-frontend/stdlib';
import { isObject } from '@direct-frontend/stdlib';
import { emitErrorToast } from '@adv-frontend/toast';
import { errorQueue } from './queues';
import styles from './toast.module.css';

type Options = {
    /**
     * Заголовок тоста. Если передан — становится title, а сообщение из error уходит в подзаголовок.
     * Без label — title выводится из error напрямую (строка / error.message / 'Ошибка').
     */
    label?: string;

    /** Длительность показа тоста. По умолчанию 5000 мс. */
    durationMs?: number;
};

type ErrorContent = {
    title: string;
    caption: string | undefined;
};

export function isCanceledError(error: unknown): boolean {
    return Boolean(
        error && typeof error === 'object' && 'isCanceled' in error && (error as Record<string, unknown>).isCanceled
    );
}

export function extractResponseCaption(error: Error): string | undefined {
    if (!('responseData' in error)) {
        return undefined;
    }

    const { responseData } = error as Error & { responseData: unknown };

    if (isString(responseData)) {
        return responseData;
    }

    if (
        isObject(responseData) &&
        'message' in responseData &&
        isString((responseData as Record<string, unknown>).message)
    ) {
        return (responseData as Record<string, string>).message;
    }

    return undefined;
}

export function resolveErrorContent(error: unknown, label?: string): ErrorContent | null {
    if (isCanceledError(error)) {
        return null;
    }

    if (typeof error === 'string') {
        return label ? { title: label, caption: error } : { title: error, caption: undefined };
    }

    if (error instanceof Error) {
        const caption = extractResponseCaption(error) ?? (label ? error.message : undefined);

        return { title: label ?? error.message, caption };
    }

    return {
        title: label ?? 'Ошибка',
        caption: error ? safeJsonStringify(error) : undefined,
    };
}

/*
 * Функция для отображения ошибки в тостере
 * Дополнительно логирует ошибку в консоль если сборка в режиме разработки
 * @param options
 * @param error
 */
export function showError(options: Options = {}, error?: unknown) {
    if (__IS_DEV__) {
        console.error(error);
    }

    const content = resolveErrorContent(error, options.label);

    if (content === null) {
        return;
    }

    const traceId = get<string>(error, ['responseData', 'trace_id']);
    const traceIdElement = traceId ? <span className={styles.text}>Trace ID: {traceId}</span> : undefined;

    // eslint-disable-next-line no-restricted-syntax -- Только здесь и можно использовать emitErrorToast
    const id = emitErrorToast(<span className={styles.text}>{content.title}</span>, {
        hasClose: true,
        caption: (
            <span className={styles.text}>
                {content.caption}
                {traceIdElement}
            </span>
        ),
        className: styles.toast,
        durationMs: options.durationMs ?? 5000,
    });

    errorQueue(id);

    return id;
}
